import {
  Calendar,
  Clock,
  CreditCard,
  Hash,
  IndianRupee,
  Mail,
  MapPin,
  Package,
  Phone,
  Scissors,
  User,
  UserCheck,
  X,
} from "lucide-react";
import { useState } from "react";
import { getTimeInAmPmFormat } from "../../../utils/formatDateTime";
import { formatDate, formatDateTime } from "../../../utils/helpers";
import { formatCurrency, roundAmount } from "../../../utils/money";
import StatusBadge from "../../common/StatusBadge";

// bookingType: "service" | "package"
const ViewBookingDetailsModal = ({
  booking,
  onClose,
  bookingType = "service",
}) => {
  const [showProofPreview, setShowProofPreview] = useState(false);
  if (!booking) return null;

  const isPackage = bookingType === "package";

  // console.log(booking)

  const {
    _id,
    userId,
    services,
    totalAmount,
    status,
    cancelReason,
    rescheduledDate,
    staffId,
    slotAvailabilityId,
    slotSnapshot,
    payment,
    location,
    createdAt,
    slotId,
    // package-specific fields
    packageSnapshot,
    packageId,
  } = booking;

  const paymentProofUrl = payment?.proof?.url;
  const paidAmount = roundAmount(payment?.paidAmount || 0);
  const finalPayable = roundAmount(booking?.finalPayableAmount || totalAmount || 0);
  const remainingAmount = roundAmount(finalPayable - paidAmount);
  const manualDiscount = roundAmount(booking?.manualDiscount || 0);
  const couponCode = booking?.coupon?.code || null;
  const couponDiscount = roundAmount(booking?.coupon?.discountApplied || 0);

  // ── Service booking computations ──
  const serviceTotals = !isPackage
    ? (services || []).map((s) => {
      const original = Number(s.originalPrice || 0) * s.qty;
      const final = Number(s.finalPrice || 0) * s.qty;
      return {
        ...s,
        originalTotal: roundAmount(original),
        finalTotal: roundAmount(final),
        discountTotal: roundAmount(original - final),
      };
    })
    : [];

  const computedOriginalTotal = roundAmount(
    serviceTotals.reduce((sum, s) => sum + s.originalTotal, 0),
  );
  const computedFinalTotal = roundAmount(
    serviceTotals.reduce((sum, s) => sum + s.finalTotal, 0),
  );
  const computedDiscount = roundAmount(computedOriginalTotal - computedFinalTotal);

  // ── Package computations ──
  const pkgName = packageSnapshot?.name || packageId?.name || "Package";
  const pkgCategory = packageSnapshot?.category || packageId?.category || "";
  const pkgServices = packageSnapshot?.services || packageId?.services || [];
  const pkgOriginalPrice = packageSnapshot?.price || 0;
  const pkgDiscountedPrice = packageSnapshot?.discountedPrice || pkgOriginalPrice;
  const pkgSavings = roundAmount(pkgOriginalPrice - pkgDiscountedPrice);

  const slotTime = slotSnapshot
    ? `${getTimeInAmPmFormat(slotSnapshot.startTime)} – ${getTimeInAmPmFormat(slotSnapshot.endTime)}`
    : "—";

  const address = location?.address;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white w-full max-w-5xl rounded-2xl max-h-[92vh] overflow-hidden shadow-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* HEADER */}
        <div className="relative bg-gradient-to-r from-accentTextColor/60 to-brandAccentColor p-6 text-white">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 rounded-lg hover:bg-white/20"
          >
            <X />
          </button>

          <div className="space-y-2">
            <div className="flex items-center gap-3">
              {isPackage ? <Package /> : <Calendar />}
              <h2 className="text-xl font-bold">
                {isPackage ? "Package Booking Details" : "Service Booking Details"}
              </h2>
              <StatusBadge status={status} />
            </div>

            <div className="flex flex-wrap gap-3 text-xs">
              <span className="bg-white/20 px-3 py-1 uppercase rounded-full flex items-center gap-1">
                <Hash size={12} /> {_id}
              </span>
              <span className="bg-white/20 uppercase px-3 py-1 rounded-full">
                Booked At: {formatDateTime(createdAt)}
              </span>
              {isPackage && pkgCategory && (
                <span className="bg-white/20 px-3 py-1 rounded-full capitalize">
                  {pkgCategory}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* CONTENT */}
        <div className="flex-1 overflow-y-auto p-6 space-y-8">

          {/* CUSTOMER + ADDRESS */}
          <section className="grid md:grid-cols-2 gap-6 items-stretch">
            <div className="space-y-4 h-full flex flex-col">
              <h3 className="font-bold text-gray-800 flex items-center gap-2">
                <User /> Customer
              </h3>
              <div className="bg-gray-50 rounded-xl p-4 space-y-2 flex-1">
                <p className="font-semibold">{userId?.name}</p>
                <p className="text-sm flex items-center gap-2">
                  <Phone size={14} /> {userId?.phoneNumber}
                </p>
                {userId?.email && (
                  <p className="text-sm flex items-center gap-2">
                    <Mail size={14} /> {userId.email}
                  </p>
                )}
              </div>
            </div>

            {address && (
              <div className="space-y-3 h-full flex flex-col">
                <h3 className="font-bold text-gray-800 flex items-center gap-2">
                  <MapPin /> Service Location
                </h3>
                <div className="bg-gray-50 rounded-xl p-4 text-sm space-y-1 flex-1">
                  {address.formatted && <p className="font-semibold">{address.formatted}</p>}
                  {(address.locality || address.city) && (
                    <p className="text-gray-600">
                      {address.locality}{address.locality && address.city ? ", " : ""}{address.city}
                    </p>
                  )}
                  {(address.state || address.pincode) && (
                    <p className="text-gray-600">
                      {address.state}{address.pincode ? ` – ${address.pincode}` : ""}
                    </p>
                  )}
                </div>
              </div>
            )}
          </section>

          {/* ── PACKAGE SUMMARY (package bookings only) ── */}
          {isPackage && (
            <section className="space-y-4">
              <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                <Package size={18} /> Package Details
              </h3>

              <div className="rounded-xl border bg-white p-5 space-y-4">
                {/* Name + Category */}
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-lg font-bold text-gray-900">{pkgName}</p>
                    {pkgCategory && (
                      <p className="text-xs text-gray-400 uppercase tracking-wide mt-0.5">{pkgCategory}</p>
                    )}
                  </div>
                  <div className="text-right">
                    {pkgSavings > 0 && (
                      <p className="text-xs line-through text-gray-400">
                        {formatCurrency(pkgOriginalPrice)}
                      </p>
                    )}

                    <p className="text-lg font-bold text-green-700">
                      {formatCurrency(pkgDiscountedPrice)}
                    </p>
                    {pkgSavings > 0 && (
                      <p className="text-xs text-red-500">Saved {formatCurrency(pkgSavings)}</p>
                    )}
                    {couponDiscount > 0 && (
                      <p className="text-red-600">
                        <span className="text-gray-500">
                          Coupon {couponCode && `(${couponCode})`}:
                        </span>{" "}
                        −{formatCurrency(couponDiscount)}
                      </p>
                    )}
                  </div>
                </div>

                {/* Included Services */}
                {pkgServices.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                      Included Services ({pkgServices.length})
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {pkgServices.map((svc, i) => (
                        <span
                          key={i}
                          className="px-3 py-1 bg-accentTextColor/10 text-accentTextColor text-xs font-medium rounded-full border border-accentTextColor/20"
                        >
                          ✓ {svc}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </section>
          )}

          {/* ── SERVICES (service bookings only) ── */}
          {!isPackage && (
            <section className="space-y-4">
              <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                <Scissors /> Services ({(services || []).length})
              </h3>
              <div className="divide-y rounded-xl border bg-white">
                {serviceTotals.map((s) => (
                  <div key={s._id} className="p-4 grid grid-cols-3 gap-4">
                    <div>
                      <p className="font-semibold">{s.serviceId?.name}</p>
                      <p className="text-xs text-gray-500">
                        {s.serviceId?.categoryId?.categoryName} →{" "}
                        {s.serviceId?.subCategoryId?.subCategoryName}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        <Clock size={12} className="inline" /> {s.serviceId?.duration}
                      </p>
                    </div>
                    <div className="text-sm text-gray-600">
                      <p>Qty: {s.qty}</p>
                      <p>Brand: {s.brand || "—"}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs line-through text-gray-400">
                        {formatCurrency(s.originalTotal)}
                      </p>
                      <p className="font-semibold text-green-700">
                        {formatCurrency(s.finalTotal)}
                      </p>
                      {s.discountTotal > 0 && (
                        <p className="text-xs text-red-600">
                          Saved {formatCurrency(s.discountTotal)}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* SLOT + PAYMENT + AMOUNT */}
          <section className="grid md:grid-cols-3 gap-6 items-stretch">
            {/* SLOT */}
            <div className="bg-gray-50 rounded-xl p-5 flex flex-col justify-between border">
              <div>
                <h3 className="font-semibold text-gray-800 flex items-center gap-2 mb-3">
                  <Calendar size={18} /> Slot Details
                </h3>
                {slotAvailabilityId?.date && (
                  <p className="text-sm text-gray-700">
                    <span className="text-gray-500">Date:</span>{" "}
                    {formatDate(slotAvailabilityId.date)}
                  </p>
                )}
                {slotTime && (
                  <p className="text-sm text-gray-700">
                    <span className="text-gray-500">Time:</span> {slotTime}
                  </p>
                )}
              </div>
              {slotId && (
                <p className="text-xs text-gray-400 mt-4 break-all">Slot ID: {slotId}</p>
              )}
            </div>

            {/* PAYMENT */}
            <div className="bg-white rounded-xl p-5 border shadow-sm flex flex-col justify-between">

              {/* HEADER */}
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                  <CreditCard size={18} /> Payment Details
                </h3>
                <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-600">
                  {(payment?.status || "—")
                    .toLowerCase()
                    .split("_")
                    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(" ")}
                </span>
              </div>

              {/* METHOD */}
              <div className="flex justify-between text-sm mb-3">
                <span className="text-gray-500">Method</span>
                <span className="font-medium text-gray-800">
                  {payment?.method === "UPI_QR"
                    ? "UPI (QR)"
                    : payment?.method === "PAY_AFTER_SERVICE"
                      ? "Pay After Service"
                      : "—"}
                </span>
              </div>

              {/* AMOUNTS */}
              <div className="bg-gray-50 rounded-lg p-4 space-y-2 text-sm">

                <div className="flex justify-between">
                  <span className="text-gray-500">Final Payable</span>
                  <span className="font-semibold text-gray-900">
                    {formatCurrency(finalPayable)}
                  </span>
                </div>

                <div className="flex justify-between text-green-700">
                  <span>Paid</span>
                  <span className="font-medium">
                    {formatCurrency(paidAmount)}
                  </span>
                </div>

                {remainingAmount > 0 && (
                  <div className="flex justify-between text-red-600">
                    <span>Remaining</span>
                    <span className="font-medium">
                      {formatCurrency(remainingAmount)}
                    </span>
                  </div>
                )}
              </div>

              {/* FOOTER */}
              <div className="mt-4 space-y-2">

                {payment?.verifiedAt && (
                  <p className="text-xs text-gray-400">
                    Verified at: {formatDateTime(payment.verifiedAt)}
                  </p>
                )}

                {paymentProofUrl && (
                  <button
                    onClick={() => setShowProofPreview(true)}
                    className="text-sm text-blue-600 hover:underline"
                  >
                    View Payment Proof
                  </button>
                )}
              </div>
            </div>

            {/* AMOUNT SUMMARY */}
            <div className="bg-white rounded-xl p-5 flex flex-col justify-between border border-green-200">
              <div>
                <h3 className="font-semibold text-green-800 flex items-center gap-2 mb-3">
                  <IndianRupee size={18} /> Amount Summary
                </h3>
                <div className="space-y-1 text-sm text-gray-700">
                  {isPackage ? (
                    <>
                      <p>
                        <span className="text-gray-500">Package Price:</span>{" "}
                        {formatCurrency(pkgOriginalPrice)}
                      </p>
                      {pkgSavings > 0 && (
                        <p className="text-red-600">
                          <span className="text-gray-500">Package Discount:</span>{" "}
                          −{formatCurrency(pkgSavings)}
                        </p>
                      )}
                      {manualDiscount > 0 && (
                        <p className="text-red-600">
                          <span className="text-gray-500">Staff Discount:</span>{" "}
                          −{formatCurrency(manualDiscount)}
                        </p>
                      )}
                    </>
                  ) : (
                    <div className="space-y-2 text-sm text-gray-700">

                      {/* ORIGINAL TOTAL */}
                      <div className="flex justify-between">
                        <span className="text-gray-500">Original Total</span>
                        <span className="font-medium">
                          {formatCurrency(computedOriginalTotal)}
                        </span>
                      </div>

                      {/* SERVICE DISCOUNT */}
                      {computedDiscount > 0 && (
                        <div className="flex justify-between text-red-600">
                          <span>Service Discount</span>
                          <span>−{formatCurrency(computedDiscount)}</span>
                        </div>
                      )}

                      {/* AFTER SERVICE */}
                      <div className="flex justify-between border-t pt-2">
                        <span className="text-gray-500">After Service Discount</span>
                        <span className="font-semibold">
                          {formatCurrency(computedFinalTotal)}
                        </span>
                      </div>

                      {/* COUPON */}
                      {couponDiscount > 0 && (
                        <div className="flex justify-between text-red-600">
                          <span>
                            Coupon {couponCode && `(${couponCode})`}
                          </span>
                          <span>−{formatCurrency(couponDiscount)}</span>
                        </div>
                      )}

                      {/* MANUAL */}
                      {manualDiscount > 0 && (
                        <div className="flex justify-between text-red-600">
                          <span>Manual Discount</span>
                          <span>−{formatCurrency(manualDiscount)}</span>
                        </div>
                      )}

                      {/* FINAL */}
                      <div className="flex justify-between border-t pt-3 mt-2">
                        <span className="text-xs text-gray-500 uppercase tracking-wide">
                          Final Payable
                        </span>
                        <span className="text-lg font-bold text-green-700">
                          {formatCurrency(finalPayable)}
                        </span>
                      </div>

                    </div>
                  )}
                </div>
              </div>
              <div className="mt-4 pt-3 border-t">
                <p className="text-xs text-gray-500 uppercase">Final Payable</p>
                <div className="text-2xl font-bold text-green-900">
                  <p className="text-2xl font-bold text-green-900">
                    {formatCurrency(finalPayable)}
                  </p>
                </div>
                {paidAmount > 0 && (
                  <div className="mt-2 text-xs text-gray-600">
                    Paid: {formatCurrency(paidAmount)} | Remaining:{" "}
                    {formatCurrency(remainingAmount)}
                  </div>
                )}
              </div>
            </div>
          </section>

          {/* STAFF */}
          <section className="bg-indigo-50 border border-indigo-200 rounded-xl p-5 flex items-start gap-4">
            <div className="p-2 rounded-lg bg-indigo-100 text-indigo-700">
              <UserCheck size={22} />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-indigo-900 text-sm uppercase tracking-wide">
                Assigned Staff
              </h3>
              {staffId?.name ? (
                <>
                  <p className="mt-1 font-semibold text-gray-900">{staffId.name}</p>
                  <p className="text-xs text-green-700 mt-0.5">Staff assigned to this booking</p>
                </>
              ) : (
                <>
                  <p className="mt-1 font-medium text-gray-600 italic">Not Assigned</p>
                  <p className="text-xs text-gray-500 mt-0.5">Awaiting staff assignment</p>
                </>
              )}
            </div>
          </section>

          {/* CANCEL REASON */}
          {status === "cancelled" && cancelReason && (
            <section className="bg-red-50 border border-red-200 rounded-xl p-4">
              <p className="font-bold text-red-700">Cancellation Reason</p>
              <p className="text-sm">{cancelReason}</p>
            </section>
          )}
        </div>

        {/* FOOTER */}
        <div className="border-t px-6 py-4 bg-gray-50 flex justify-end">
          <button
            onClick={onClose}
            className="px-6 py-2.5 rounded-lg text-sm font-medium text-gray-700 border border-gray-300 bg-white hover:bg-gray-100 transition"
          >
            Close
          </button>
        </div>
      </div>

      {showProofPreview && (
        <div
          className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-4"
          onClick={() => setShowProofPreview(false)}
        >
          <div
            className="relative max-w-3xl w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowProofPreview(false)}
              className="absolute -top-10 right-0 text-white flex items-center gap-1 text-sm"
            >
              <X size={18} /> Close
            </button>
            <img
              src={paymentProofUrl}
              alt="Payment Proof Full View"
              className="w-full max-h-[80vh] object-contain rounded-xl bg-black"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewBookingDetailsModal;



